import { orderToLetter } from '../../utils/common-utils.ts';
import {
  Maybe,
  QuestionAnswerType,
  TestAttemptModuleLevel,
  TestSectionModuleLevel,
} from '../../apollo/__generated__/graphql.ts';
import { useMemo } from 'react';
import { mean, orderBy } from 'lodash';
import { useQuery } from '@apollo/client';
import {
  AttemptCountItem,
  GET_QUIZ_VIEW_DATA,
  GET_TEST_VIEW_DATA,
  QuizViewAttempt,
  TestViewAttempt,
} from '../../apollo/answers-view.ts';

interface IPerformanceItem {
  moduleLevel?: TestSectionModuleLevel;
  questionId: number;
  performance: number | null;
}

interface IQuestionData {
  id: number;
  domain: string;
  skill: string;
  type: string;
  subject: string;
  answer: string;
}
interface ITestData {
  sections: {
    name: string;
    modules: {
      sectionName: string;
      level: TestSectionModuleLevel;
      questions: IQuestionData[];
    }[];
  }[];
}

interface IUserData {
  attemptId: number;
  rwModuleLevel?: Maybe<TestAttemptModuleLevel>;
  mathModuleLevel?: Maybe<TestAttemptModuleLevel>;
  score: number;
  userName: string;
  userId: number;
  createdAt: Date;
  answers: {
    id: number;
    answer: string;
    isCorrect: boolean | undefined | null;
  }[];
}

export const useGetTestViewData = (
  testId?: number,
  requestedUserIds?: number[] | undefined,
  visibleUserIds?: number[] | undefined,
  attemptOverridesByUserId?: Record<number, TestViewAttempt | undefined>,
) => {
  const { data, loading, refetch } = useQuery(GET_TEST_VIEW_DATA, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !testId,
    variables: {
      input: {
        id: testId ?? 0,
      },
      attemptsFilter: {
        userIds: requestedUserIds ?? [],
      },
    },
  });
  const modulesOptions: string[] = useMemo(() => {
    const options: string[] = [];
    const sections = data?.test.getTest.sections;
    sections?.forEach((section) => {
      section.modules.forEach((module) => {
        options.push(`${section.section.name} - ${module.level}`);
      });
    });
    return options;
  }, [data?.test.getTest]);

  const completedAttemptsCounts = useMemo(() => {
    return (
      data?.test.getTest.completedAttemptsCounts?.reduce<Record<number, number>>(
        (acc, item: AttemptCountItem) => {
          acc[item.userId] = item.count;
          return acc;
        },
        {},
      ) ?? {}
    );
  }, [data?.test.getTest.completedAttemptsCounts]);

  const testAttempts: IUserData[] = useMemo(() => {
    const testAttemptsArray: IUserData[] = [];
    const activeUserIds = visibleUserIds ?? requestedUserIds;
    activeUserIds?.forEach((userId) => {
      const attempt =
        attemptOverridesByUserId?.[userId] ??
        data?.test.getTest.latestCompletedAttempts.find(
          (item) => item.createdBy?.id === userId,
        );

      if (attempt) {
        testAttemptsArray.push({
          attemptId: attempt.id,
          score: 0,
          rwModuleLevel: attempt.rwModuleLevel,
          mathModuleLevel: attempt.mathModuleLevel,
          userName: attempt.createdBy!.name || attempt.createdBy!.email,
          userId: attempt.createdBy!.id,
          createdAt: attempt.createdAt,
          answers: attempt.questions.map((q) => {
            const isAnswerExist = !!q.text || !!q.choice;
            return {
              id: q.questionId,
              isCorrect: q.correct,
              answer: isAnswerExist
                ? q.text || orderToLetter(q.choice!.order)
                : '',
            };
          }),
        });
      }
    });
    return testAttemptsArray;
  }, [
    attemptOverridesByUserId,
    data?.test.getTest.latestCompletedAttempts,
    requestedUserIds,
    visibleUserIds,
  ]);

  const test: ITestData = useMemo(() => {
    const sections = data?.test.getTest.sections;
    if (!sections) return {} as ITestData;
    return {
      sections: sections!.map((section) => {
        return {
          name: section.section.name,
          modules: section.modules.map((m) => {
            return {
              sectionName: section.section.name,
              level: m.level,
              questions: orderBy(m.questions, (item) => item.order).map(
                ({ question }) => {
                  const correctChoice = question.choices.find(
                    (choice) => choice.correct,
                  );
                  return {
                    id: question.id,
                    domain: question.domain?.name || '',
                    skill: question.skill?.name || '',
                    type: question.type1?.name || '',
                    subject: question.subject?.name || '',
                    answer:
                      question.answerType === QuestionAnswerType.TextEntry
                        ? correctChoice?.text + ''
                        : orderToLetter(correctChoice!.order),
                  };
                },
              ),
            };
          }),
        };
      }),
    };
  }, [data?.test.getTest.sections]);

  const testPerformance: IPerformanceItem[] = useMemo(() => {
    const temp: IPerformanceItem[] = [];
    test?.sections?.forEach((section) => {
      section.modules.forEach((module) => {
        module.questions.forEach((question) => {
          const answerPerformance: number[] = [];
          testAttempts.forEach((attempt) => {
            const isMathAdaptiveLevelWrong =
              module.sectionName === 'math' &&
              module.level !== TestSectionModuleLevel.All &&
              TestAttemptModuleLevel[module.level] !== attempt?.mathModuleLevel;
            const isReadingAdaptiveLevelWrong =
              module.sectionName === 'reading and writing' &&
              module.level !== TestSectionModuleLevel.All &&
              TestAttemptModuleLevel[module.level] !== attempt?.rwModuleLevel;

            attempt.answers.forEach((answer) => {
              if (
                answer.id === question.id &&
                !isMathAdaptiveLevelWrong &&
                !isReadingAdaptiveLevelWrong
              ) {
                answer.isCorrect
                  ? answerPerformance.push(1)
                  : answerPerformance.push(0);
              }
            });
          });
          temp.push({
            moduleLevel: module.level,
            questionId: Number(question.id),
            performance: mean(answerPerformance) * 100,
          });
        });
      });
    });
    return temp;
  }, [test?.sections, testAttempts]);
  return {
    test,
    testAttempts,
    modulesOptions,
    testPerformance,
    completedAttemptsCounts,
    loading,
    refetch,
  };
};

export const useGetQuizViewData = (
  quizId?: number,
  requestedUserIds?: number[],
  visibleUserIds?: number[],
  attemptOverridesByUserId?: Record<number, QuizViewAttempt | undefined>,
) => {
  const { data, loading, refetch } = useQuery(GET_QUIZ_VIEW_DATA, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !quizId,
    variables: {
      input: {
        id: quizId ?? 0,
      },
      attemptsFilter: {
        userIds: requestedUserIds ?? [],
      },
    },
  });

  const completedAttemptsCounts = useMemo(() => {
    return (
      data?.quiz.getQuiz.completedAttemptsCounts?.reduce<Record<number, number>>(
        (acc, item: AttemptCountItem) => {
          acc[item.userId] = item.count;
          return acc;
        },
        {},
      ) ?? {}
    );
  }, [data?.quiz.getQuiz.completedAttemptsCounts]);

  const quizAttempts: IUserData[] = useMemo(() => {
    const quizAttemptsArray: IUserData[] = [];
    const activeUserIds = visibleUserIds ?? requestedUserIds;
    activeUserIds?.forEach((userId) => {
      const attempt =
        attemptOverridesByUserId?.[userId] ??
        data?.quiz.getQuiz.latestCompletedAttempts.find(
          (item) => item.createdBy?.id === userId,
        );

      if (attempt) {
        quizAttemptsArray.push({
          attemptId: attempt.id,
          score: attempt.score ?? 0,
          userName: attempt.createdBy!.name || attempt.createdBy!.email,
          userId: attempt.createdBy!.id,
          createdAt: attempt.createdAt,
          answers: attempt.questions.map((q) => {
            const isAnswerExist = !!q.text || !!q.choice;
            return {
              id: q.questionId,
              isCorrect: !!q.correct,
              answer: isAnswerExist
                ? q.text || orderToLetter(q.choice!.order)
                : '',
            };
          }),
        });
      }
    });
    return quizAttemptsArray;
  }, [
    attemptOverridesByUserId,
    data?.quiz.getQuiz.latestCompletedAttempts,
    requestedUserIds,
    visibleUserIds,
  ]);

  const quizQuestionsSort = useMemo(() => {
    return orderBy(
      data?.quiz.getQuiz.quizQuestions,
      (item) => item.order,
      'asc',
    );
  }, [data?.quiz.getQuiz.quizQuestions]);

  const quizPerformance: IPerformanceItem[] = useMemo(() => {
    const performance: IPerformanceItem[] = [];
    quizQuestionsSort.forEach((question) => {
      const array: number[] = [];
      quizAttempts.forEach((attempt) => {
        const isCorrect = attempt.answers.find(
          (answer) => answer.id === question.question?.id,
        )?.isCorrect;
        array.push(isCorrect ? 1 : 0);
      });

      if (question.question?.id)
        performance.push({
          questionId: Number(question.question.id),
          performance: mean(array) * 100,
        });
    });

    return performance;
  }, [quizAttempts, quizQuestionsSort]);

  return {
    quizAttempts,
    quizQuestionsSort,
    quizPerformance,
    completedAttemptsCounts,
    loading,
    refetch,
  };
};
